import React from "react";
import { motion } from "framer-motion";
import { Building2, Sparkles, Users, CalendarDays, ChevronRight, ChevronLeft, AlertTriangle, Download } from "lucide-react";
import { exportWorkSummary } from "../exportPdf";
import { useDashTheme } from "../../../hooks/useDashTheme";
import { Card } from "../../../components/primitives";
import { EmptyState } from "../../../components/DataTable";
import AntDropdown from "../../../../../components/UI/AntDropdown";
import AntDatePicker from "../../../../../components/UI/AntDatePicker";
import { SERVER_URL } from "../../../../../utils/serverConfig";
import { CATS, useCatColors, fmtDur } from "../bits";
import { SectionFrame, Sk } from "../story/storyBits";
import { DayClock, WhatWentInto, DayClockSk, WhatWentIntoSk } from "../story/DayShape";
import { ToolIcon } from "../../../components/classic/panels";

/**
 * DepartmentStory — the Work Summary page one level up.
 *
 * Pick a department → Generate → the department's day, told the way a
 * person's day is told: a verdict, the shape (the SAME clock and ring the
 * person page draws, fed the department's blocks added up), the four written
 * sections MRF already reads (on demand, one credit, cached), the people, and
 * the accounting. Clicking a person hands off to the existing person view —
 * nothing here re-implements a drawer.
 *
 * Everything except the brief is deterministic and free (GET /department).
 */

const fmtDate = (d) => {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
const shiftDay = (iso, n) => { const [y, m, d] = iso.split("-").map(Number); const dt = new Date(y, m - 1, d + n); return fmtDate(dt); };
const eyebrowFor = (iso) => {
  const [y, m, d] = iso.split("-").map(Number); const dt = new Date(y, m - 1, d);
  return `${dt.toLocaleDateString(undefined, { weekday: "long" })} · ${d} ${dt.toLocaleDateString(undefined, { month: "long" })}`.toUpperCase();
};
const initials = (n = "") => n.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "·";
const CAT_WORD = { deep: "building", docs: "writing", browse: "research", comms: "meetings", review: "review", distract: "off-task", idle: "away" };

function Chip({ children, dot, tone }) {
  const t = useDashTheme();
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap",
      fontSize: t.font.size.sm, fontWeight: t.font.weight.medium,
      color: tone || t.c.textSecondary,
      background: tone ? `${tone}1f` : (t.isDark ? t.c.surfaceMuted : t.c.surfaceHover),
      border: `1px solid ${tone ? `${tone}45` : t.c.border}`,
      borderRadius: t.r.pill, padding: "5px 13px", fontVariantNumeric: "tabular-nums",
    }}>
      {dot && <i style={{ width: 7, height: 7, borderRadius: "50%", background: dot }} />}
      {children}
    </span>
  );
}

function VerdictPill({ kind }) {
  const t = useDashTheme();
  const map = {
    look: { c: t.status.warning, l: "Worth a look" },
    ok: { c: t.status.positive, l: "Settled" },
    thin: { c: t.c.textTertiary, l: "Thin" },
  };
  const m = map[kind] || map.ok;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 600, padding: "4px 10px", borderRadius: 99, color: m.c, background: `${m.c}1f` }}>
      <i style={{ width: 6, height: 6, borderRadius: "50%", background: m.c }} />{m.l}
    </span>
  );
}

/* ── the AI brief (SSE) ───────────────────────────────────────────────── */
function useDepartmentBrief() {
  const [state, setState] = React.useState({ running: false, headline: null, sections: {}, persons: [], error: null, cached: false, done: false });
  const abortRef = React.useRef(null);
  const run = React.useCallback(async ({ department, date, source, force }) => {
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController(); abortRef.current = ac;
    setState({ running: true, headline: null, sections: {}, persons: [], error: null, cached: false, done: false });
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${SERVER_URL}/api/v1/work-summary-new/department-brief`, {
        method: "POST", signal: ac.signal,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ department, date, source, force: !!force }),
      });
      if (!res.ok || !res.body) {
        let msg = "Could not write the brief.";
        try { const j = await res.json(); msg = j?.message || msg; } catch (_) { /* stream */ }
        setState((s) => ({ ...s, running: false, error: msg, done: true }));
        return;
      }
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n"); buf = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          let d; try { d = JSON.parse(line.slice(6)); } catch (_) { continue; }
          if (d.type === "headline") setState((s) => ({ ...s, headline: d.text, cached: !!d.cached }));
          else if (d.type === "section") setState((s) => ({ ...s, sections: { ...s.sections, [d.key]: d }, cached: !!d.cached || s.cached }));
          else if (d.type === "person") setState((s) => ({ ...s, persons: [...s.persons.filter((x) => x.name !== d.name), d] }));
          else if (d.type === "error") setState((s) => ({ ...s, error: d.message }));
          else if (d.type === "done") setState((s) => ({ ...s, running: false, done: true }));
        }
      }
      setState((s) => ({ ...s, running: false, done: true }));
    } catch (e) {
      if (e.name !== "AbortError") setState((s) => ({ ...s, running: false, error: e.message || "Could not write the brief.", done: true }));
    }
  }, []);
  const reset = React.useCallback(() => { if (abortRef.current) abortRef.current.abort(); setState({ running: false, headline: null, sections: {}, persons: [], error: null, cached: false, done: false }); }, []);
  return { ...state, run, reset };
}

const SECTION_META = (t) => ({
  delivered: { title: "What got done", glyph: "✓", tone: t.c.primary },
  streams: { title: "Where the hours went", glyph: "◔", tone: t.c.ai },
  wins: { title: "Wins", glyph: "↗", tone: t.status.positive },
  attention: { title: "Needs attention", glyph: "!", tone: t.status.warning },
});
const SECTION_ORDER = ["delivered", "wins", "attention"];

function BriefSection({ k, sec, sf, onName, people }) {
  const t = useDashTheme();
  const meta = SECTION_META(t)[k];
  // Names inside the prose become links to the person — the drill-down is a
  // click on a name, exactly as the mock promised.
  const linkify = (text) => {
    if (!text) return text;
    const names = (people || []).map((p) => p.name).filter((n) => n && n.length > 2).sort((a, b) => b.length - a.length);
    const parts = []; let rest = text; let i = 0;
    while (rest.length) {
      let hit = null;
      for (const n of names) { const idx = rest.indexOf(n); if (idx >= 0 && (hit === null || idx < hit.idx)) hit = { idx, n }; }
      const first = (() => { for (const n of names) { const idx = rest.indexOf(n.split(" ")[0]); if (idx >= 0 && (hit === null || idx < hit.idx)) return { idx, n, short: n.split(" ")[0] }; } return null; })();
      const use = first && (!hit || first.idx < hit.idx) ? first : hit;
      if (!use) { parts.push(rest); break; }
      const label = use.short || use.n;
      if (use.idx > 0) parts.push(rest.slice(0, use.idx));
      parts.push(<button key={`n${i++}`} type="button" className="st-reset" onClick={() => onName(use.n)} style={{ color: t.c.primary, fontWeight: 600, cursor: "pointer", background: "none", border: 0, padding: 0, font: "inherit" }}>{label}</button>);
      rest = rest.slice(use.idx + label.length);
    }
    return parts;
  };
  // "<thing> — <who> · <time>" → thing (strong) + meta (muted, names linked).
  const splitBullet = (b) => {
    const i = b.lastIndexOf(" — ");
    if (i < 0) return { thing: b, meta: "" };
    return { thing: b.slice(0, i), meta: b.slice(i + 3) };
  };
  return (
    <Card style={{ border: `1px solid ${sf.cardBorder}`, boxShadow: sf.cardShadow }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 10 }}>
        <span style={{ width: 24, height: 24, borderRadius: 7, display: "grid", placeItems: "center", fontSize: 12, fontWeight: 700, color: meta.tone, background: `${meta.tone}22` }}>{meta.glyph}</span>
        <span style={{ fontSize: 14, fontWeight: 600, color: t.c.text }}>{sec?.title || meta.title}</span>
      </div>
      {!sec ? (
        <div><Sk w="92%" h={13} style={{ marginBottom: 9 }} /><Sk w="76%" h={13} style={{ marginBottom: 9 }} /><Sk w="60%" h={13} /></div>
      ) : sec.items ? (
        <div style={{ display: "grid", gap: 6 }}>
          {sec.items.map((it, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10, alignItems: "center", padding: "7px 10px", borderRadius: 8, background: t.c.surfaceMuted }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: t.c.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.label}</div>
                <div style={{ fontSize: 11.5, color: t.c.textTertiary, marginTop: 1 }}>{linkify((it.people || []).join(", "))}</div>
              </div>
              <span style={{ fontSize: 12.5, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: meta.tone, whiteSpace: "nowrap" }}>{it.hours}</span>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ display: "grid", gap: 7 }}>
          {(sec.bullets || []).map((b, i) => {
            const { thing, meta: m } = splitBullet(b);
            return (
              <div key={i} style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: meta.tone, marginTop: 7, flex: "none" }} />
                <div style={{ fontSize: 13.5, lineHeight: 1.5 }}>
                  <span style={{ color: t.c.text, fontWeight: 500 }}>{thing}</span>
                  {m && <span style={{ color: t.c.textTertiary, fontSize: 12.5 }}> — {linkify(m)}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

/* ── the screen ───────────────────────────────────────────────────────── */
export function DepartmentStory({ date, onDate, source = "both", sf, onOpenPerson, initialDepartment }) {
  const t = useDashTheme();
  const C = useCatColors();
  const [departments, setDepartments] = React.useState([]);
  const [loadingDepts, setLoadingDepts] = React.useState(true);
  const [department, setDepartment] = React.useState(initialDepartment || null);
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [generatedKey, setGeneratedKey] = React.useState(null);
  const [showSilent, setShowSilent] = React.useState(false);
  const brief = useDepartmentBrief();
  const [exporting, setExporting] = React.useState(false);
  const [exportNote, setExportNote] = React.useState(null);
  const runExport = React.useCallback(async () => {
    if (!department || exporting) return;
    setExporting(true); setExportNote(null);
    try { const f = await exportWorkSummary({ kind: "department", department, date, source, label: department }); setExportNote(`Saved ${f}`); }
    catch (e) { setExportNote(e.message || "Export failed."); }
    finally { setExporting(false); setTimeout(() => setExportNote(null), 6000); }
  }, [department, date, source, exporting]);
  const key = `${department}|${date}|${source}`;
  const stale = generatedKey && generatedKey !== key;

  React.useEffect(() => {
    (async () => {
      setLoadingDepts(true);
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${SERVER_URL}/api/v1/work-summary-new/departments`, { headers: { Authorization: `Bearer ${token}` } });
        const json = await res.json();
        const list = json?.data?.departments || [];
        setDepartments(list.map((d) => ({ value: d.name, label: d.name, sub: `${d.count} ${d.count === 1 ? "person" : "people"}`, count: d.count })));
        if (!department && list.length) setDepartment(list[0].name);
      } catch (_) { /* picker stays empty */ }
      finally { setLoadingDepts(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generate = React.useCallback(async (refresh) => {
    if (!department) return;
    setLoading(true); setError(null); brief.reset();
    try {
      const token = localStorage.getItem("token");
      const qs = new URLSearchParams({ department, date, source, ...(refresh ? { refresh: "1" } : {}) });
      const res = await fetch(`${SERVER_URL}/api/v1/work-summary-new/department?${qs}`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.message || "Could not build the department day.");
      setData(json.data); setGeneratedKey(key);
      // The report IS the brief: write it as part of Generate (cached for the
      // day, so re-opening never spends a second credit).
      if (json.data?.totals?.tracked > 0) brief.run({ department, date, source, force: false });
    } catch (e) { setError(e.message || "Could not build the department day."); setData(null); }
    finally { setLoading(false); }
  }, [department, date, source, key, brief]);

  const totals = data?.totals;
  const deptMeta = departments.find((d) => d.value === department);
  const topCatEntry = data ? Object.entries(data.cats || {}).sort((a, b) => b[1] - a[1])[0] : null;

  const people = React.useMemo(() => {
    const list = data?.people || [];
    return showSilent ? [...list, ...(data?.silent || []).map((s) => ({ ...s, silent: true }))] : list;
  }, [data, showSilent]);

  const openByName = (name) => {
    const p = (data?.people || []).find((x) => x.name === name);
    if (p) onOpenPerson({ id: p.id, name: p.name, department });
  };

  return (
    <React.Fragment>
      {/* ── the picker band ─────────────────────────────────────────────── */}
      <Card style={{ position: "relative", overflow: "hidden", border: `1px solid ${sf.cardBorder}`, boxShadow: sf.cardShadow, marginBottom: t.s[4] }}>
        <div aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none", background: sf.wash(t.c.ai) }} />
        <div style={{ position: "relative", display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{
            width: 54, height: 54, borderRadius: 16, display: "grid", placeItems: "center", flex: "none",
            background: `${t.c.ai}18`, color: t.c.ai, border: `1px solid ${t.c.ai}33`,
          }}><Building2 size={24} /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ minWidth: 240 }}>
                <AntDropdown
                  value={department || undefined} options={departments}
                  onChange={(v) => { setDepartment(v || null); }}
                  placeholder={loadingDepts ? "Loading…" : `Choose a department (${departments.length})`}
                />
              </div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <button type="button" className="st-reset st-focusable" onClick={() => onDate(shiftDay(date, -1))} title="Previous day"
                  style={{ width: 30, height: 30, borderRadius: 8, border: `1px solid ${t.c.border}`, background: t.c.surface, color: t.c.textSecondary, cursor: "pointer", display: "grid", placeItems: "center" }}><ChevronLeft size={15} /></button>
                <div style={{ width: 150 }}><AntDatePicker className="tg-dash-picker" value={date} onChange={(_d, s) => { if (s) onDate(s); }} allowClear={false} /></div>
                <button type="button" className="st-reset st-focusable" onClick={() => onDate(shiftDay(date, 1))} title="Next day"
                  style={{ width: 30, height: 30, borderRadius: 8, border: `1px solid ${t.c.border}`, background: t.c.surface, color: t.c.textSecondary, cursor: "pointer", display: "grid", placeItems: "center" }}><ChevronRight size={15} /></button>
                {[["Today", 0], ["Yesterday", -1]].map(([l, n]) => (
                  <button key={l} type="button" className="st-reset st-focusable" onClick={() => onDate(shiftDay(fmtDate(new Date()), n))}
                    style={{ fontSize: 12, fontWeight: 600, padding: "6px 10px", borderRadius: 8, border: `1px solid ${t.c.border}`, background: t.c.surface, color: t.c.textSecondary, cursor: "pointer" }}>{l}</button>
                ))}
              </div>
              <button type="button" className="st-reset st-focusable" disabled={!department || loading} onClick={() => generate(false)}
                style={{
                  marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600,
                  padding: "9px 16px", borderRadius: 9, border: "none", cursor: department && !loading ? "pointer" : "not-allowed",
                  background: t.c.primary, color: "#fff", opacity: !department || loading ? 0.6 : 1,
                }}><Sparkles size={15} /> {loading ? "Building…" : data && !stale ? "Regenerate" : "Generate report"}</button>
              <button type="button" className="st-reset st-focusable" disabled={!data || stale || loading || exporting} onClick={runExport}
                title="Export this department report as PDF — includes the written report once it has been generated"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5, fontWeight: 600, padding: "9px 13px", borderRadius: 9,
                  border: `1px solid ${t.c.border}`, background: t.c.surface, color: t.c.textSecondary,
                  cursor: !data || stale || loading || exporting ? "not-allowed" : "pointer", opacity: !data || stale || loading || exporting ? 0.55 : 1,
                }}><Download size={14} /> {exporting ? "Building PDF…" : "Export PDF"}</button>
            </div>
            <div style={{ marginTop: 10, fontSize: t.font.size.sm, color: t.c.textTertiary }}>
              {deptMeta ? `${deptMeta.label} · ${deptMeta.sub} · ${eyebrowFor(date).charAt(0) + eyebrowFor(date).slice(1).toLowerCase()}` : "Pick a department"}
              {" — one click builds the numbers and writes the brief; cached for the day."}
              {stale && <span style={{ color: t.status.warning, fontWeight: 600 }}> · selection changed — generate again</span>}
              {exportNote && <span style={{ color: t.c.textTertiary }}> · {exportNote}</span>}
            </div>
          </div>
        </div>
      </Card>

      {!data && !loading && !error && (
        <Card style={{ border: `1px solid ${sf.cardBorder}`, boxShadow: sf.cardShadow }}>
          <EmptyState icon={<Building2 size={22} />} title="Pick a department and generate its report"
            description="Every tracked person's day is added up — the same numbers you see when you open them one by one." />
        </Card>
      )}
      {error && (
        <Card style={{ border: `1px solid ${sf.cardBorder}`, boxShadow: sf.cardShadow }}>
          <EmptyState icon={<AlertTriangle size={22} />} title="Could not build this department's day" description={error} />
        </Card>
      )}

      {(loading || data) && !error && (
        <React.Fragment>
          {/* ── hero ──────────────────────────────────────────────────────── */}
          <Card style={{ position: "relative", overflow: "hidden", border: `1px solid ${sf.cardBorder}`, boxShadow: sf.cardShadow, marginBottom: t.s[4] }}>
            <div aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none", background: sf.wash(t.c.ai) }} />
            {loading ? (
              <div style={{ position: "relative" }}><Sk w={420} h={24} style={{ marginBottom: 10 }} /><Sk w="80%" h={13} style={{ marginBottom: 6 }} /><Sk w="55%" h={13} /></div>
            ) : (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: t.motion.ease.out }} style={{ position: "relative" }}>
                <div style={{ ...t.font.microLabel, color: t.c.textTertiary, letterSpacing: "0.13em" }}>{eyebrowFor(date)} · {data.department.toUpperCase()}</div>
                <div style={{ marginTop: 8, fontSize: 26, fontWeight: t.font.weight.semibold, letterSpacing: "-0.025em", lineHeight: 1.22, color: t.c.text, maxWidth: 900 }}>
                  {brief.headline || data.headline}
                </div>
                {data.coverage?.truncated && (
                  <div style={{ marginTop: 10, fontSize: 12, color: t.status.warning }}>Only the first {data.coverage.limit} people were read for this department.</div>
                )}
              </motion.div>
            )}
          </Card>

          {/* ── verdict band ──────────────────────────────────────────────── */}
          {!loading && data.worth?.length > 0 && (
            <Card padding={0} style={{ border: `1px solid ${sf.cardBorder}`, borderLeft: `4px solid ${t.status.warning}`, boxShadow: sf.cardShadow, marginBottom: t.s[4] }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "13px 18px", flexWrap: "wrap" }}>
                <b style={{ fontSize: 14, color: t.c.text }}>Worth opening</b>
                <span style={{ fontSize: 13, color: t.c.textSecondary, flex: 1, minWidth: 240 }}>
                  {data.worth.slice(0, 4).map((w, i) => (
                    <React.Fragment key={w.id}>{i > 0 && " · "}<button type="button" className="st-reset" onClick={() => onOpenPerson({ id: w.id, name: w.name, department })} style={{ color: t.c.primary, fontWeight: 600, cursor: "pointer", background: "none", border: 0, padding: 0, font: "inherit" }}>{w.name.split(" ")[0]}</button></React.Fragment>
                  ))}
                  {data.worth.length > 4 && ` · +${data.worth.length - 4} more below`}
                </span>
                <VerdictPill kind="look" />
              </div>
            </Card>
          )}
          {!loading && data.worth?.length === 0 && totals?.tracked > 0 && (
            <Card padding={0} style={{ border: `1px solid ${sf.cardBorder}`, borderLeft: `4px solid ${t.status.positive}`, boxShadow: sf.cardShadow, marginBottom: t.s[4] }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "13px 18px" }}>
                <b style={{ fontSize: 14, color: t.c.text }}>Nothing here needs a closer look</b>
                <span style={{ fontSize: 13, color: t.c.textSecondary, flex: 1 }}>Every tracked day in {data.department} settled.</span>
                <VerdictPill kind="ok" />
              </div>
            </Card>
          )}

          {!loading && totals && !totals.trackedMin ? (
            <Card style={{ border: `1px solid ${sf.cardBorder}`, boxShadow: sf.cardShadow }}>
              <EmptyState icon={<CalendarDays size={22} />} title="Nothing tracked on this day"
                description={`Neither the desktop app nor the background agent reported activity for anyone in ${data.department}.`} />
            </Card>
          ) : (
            <React.Fragment>
              {/* ── ① the brief — this IS the report ───────────────────── */}
              <SectionFrame kicker="AI brief" tone={t.c.ai} title="Department report" sub="What got done, what went well, and what needs a look — one line per person.">
              {!loading && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", marginBottom: 12, borderRadius: 12,
                  border: `1px solid ${t.c.aiBorder}`, background: t.c.aiSoft, fontSize: 12.5, color: t.c.textSecondary, flexWrap: "wrap",
                }}>
                  <Sparkles size={14} style={{ color: t.c.ai }} />
                  <span>{brief.running ? "Writing the report from the tracked days…" : brief.error ? "The report could not be written." : brief.cached ? "Read from today's cache — no credit spent." : brief.done ? "Written just now — cached for the day." : "Ready."}</span>
                  {!brief.running && (
                    <button type="button" className="st-reset st-focusable" onClick={() => brief.run({ department, date, source, force: true })}
                      style={{ marginLeft: "auto", fontSize: 12, fontWeight: 600, padding: "6px 12px", borderRadius: 8, border: `1px solid ${t.c.ai}`, background: "transparent", color: t.c.ai, cursor: "pointer" }}>
                      {brief.error ? "Try again" : "Rewrite"}
                    </button>
                  )}
                </div>
              )}
              {brief.error && <div style={{ fontSize: 12.5, color: t.status.negative, marginBottom: 12 }}>{brief.error}</div>}
              {!brief.error && (
                <div className="tg-shape-grid" style={{ marginBottom: t.s[3] }}>
                  {SECTION_ORDER.map((k) => (
                    <BriefSection key={k} k={k} sec={brief.sections[k]} sf={sf} people={data?.people} onName={openByName} />
                  ))}
                </div>
              )}
              {!brief.error && (brief.running || brief.persons.length > 0) && (
                <Card padding={0} style={{ border: `1px solid ${sf.cardBorder}`, boxShadow: sf.cardShadow, overflow: "hidden", marginBottom: t.s[4] }}>
                  <div style={{ padding: "12px 18px", borderBottom: `1px solid ${t.c.border}`, background: t.c.surfaceMuted, display: "flex", alignItems: "center", gap: 10 }}>
                    <Users size={15} style={{ color: t.c.textSecondary }} />
                    <span style={{ fontSize: 13.5, fontWeight: 600, color: t.c.text }}>People at a glance</span>
                    <span style={{ fontSize: 12, color: t.c.textTertiary }}>the concrete work, one line each · click a name for the full day</span>
                  </div>
                  {(data?.people || []).map((p, i) => {
                    const line = brief.persons.find((x) => x.id === p.id || x.name === p.name);
                    return (
                      <div key={p.id} style={{ display: "grid", gridTemplateColumns: "200px 1fr 118px", gap: 14, alignItems: "center", padding: "10px 18px", borderTop: i ? `1px solid ${t.c.border}` : "none" }}>
                        <button type="button" className="st-reset st-focusable" onClick={() => onOpenPerson({ id: p.id, name: p.name, department })}
                          style={{ display: "flex", alignItems: "center", gap: 9, cursor: "pointer", textAlign: "left" }}>
                          <span style={{ width: 26, height: 26, borderRadius: "50%", display: "grid", placeItems: "center", fontSize: 10.5, fontWeight: 700, color: "#fff", background: ["#4273D1", "#6D4AFF", "#2E9E6B", "#E8622C", "#2AA7B8", "#C98A2B", "#9B59B6", "#607D8B"][i % 8], flex: "none" }}>{initials(p.name)}</span>
                          <span><span style={{ display: "block", fontSize: 13, fontWeight: 600, color: t.c.primary }}>{p.name}</span>{p.position && <span style={{ fontSize: 11, color: t.c.textTertiary }}>{p.position}</span>}</span>
                        </button>
                        <div style={{ fontSize: 13, lineHeight: 1.55, color: t.c.textSecondary }}>{line ? line.text : <Sk w="70%" h={12} />}</div>
                        <div><VerdictPill kind={p.verdict?.kind} /></div>
                      </div>
                    );
                  })}
                </Card>
              )}
              </SectionFrame>

              {/* ── ① shape ─────────────────────────────────────────────── */}
              <SectionFrame kicker="Picture" title="How the department ran" sub="When people were at the desk, and what the hours went into — added up, no AI.">
              <div className="tg-shape-grid" style={{ padding: 20, gap: 16 }}>
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: t.c.text }}>When the department worked</div>
                  <div style={{ fontSize: 12, color: t.c.textTertiary, margin: "2px 0 10px" }}>A longer wedge is a fuller hour across everyone; its colour is what that hour went into</div>
                  <div className="tg-shape-fill">{loading ? <DayClockSk /> : <DayClock blocks={data.blocks} bounds={data.bounds} totals={totals} />}</div>
                </div>
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: t.c.text }}>What the hours went into</div>
                  <div style={{ fontSize: 12, color: t.c.textTertiary, margin: "2px 0 10px" }}>Same slices as a person's day — just added up{topCatEntry ? ` · mostly ${CAT_WORD[topCatEntry[0]] || topCatEntry[0]}` : ""}</div>
                  <div className="tg-shape-fill">{loading ? <WhatWentIntoSk /> : <WhatWentInto blocks={data.blocks} />}</div>
                </div>
              </div>
              </SectionFrame>

              {/* ── ③ people ────────────────────────────────────────────── */}
              <SectionFrame kicker="People" title={data?.department || "The department"} sub="Click a person for their full day." meta={people?.length ? `${people.length} people` : null}>
                {loading ? (
                  <div style={{ padding: 20 }}>{[0, 1, 2, 3, 4].map((i) => <Sk key={i} h={38} style={{ marginBottom: 10 }} />)}</div>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: t.c.surfaceMuted }}>
                        {["Person", "Shape of the day", "Top apps", "Verdict", ""].map((h) => (
                          <th key={h} style={{ ...t.font.microLabel, color: t.c.textTertiary, textAlign: "left", padding: "10px 16px", borderBottom: `1px solid ${t.c.border}`, fontWeight: 700 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {people.map((p, i) => {
                        const tot = Math.max(1, p.trackedMin || 0);
                        const slices = Object.entries(p.cats || {}).sort((a, b) => b[1] - a[1]);
                        const hue = ["#4273D1", "#6D4AFF", "#2E9E6B", "#E8622C", "#2AA7B8", "#C98A2B", "#9B59B6", "#607D8B"][i % 8];
                        return (
                          <tr key={p.id} className="tg-dept-row" onClick={() => !p.silent && onOpenPerson({ id: p.id, name: p.name, department })}
                            style={{ cursor: p.silent ? "default" : "pointer", opacity: p.silent ? 0.62 : 1 }}>
                            <td style={{ padding: "11px 16px", borderBottom: `1px solid ${t.c.border}` }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <span style={{ width: 28, height: 28, borderRadius: "50%", display: "grid", placeItems: "center", fontSize: 11, fontWeight: 700, color: "#fff", background: hue, flex: "none" }}>{initials(p.name)}</span>
                                <div><div style={{ fontSize: 13.5, fontWeight: 600, color: t.c.text }}>{p.name}</div>{p.position && <div style={{ fontSize: 11.5, color: t.c.textTertiary }}>{p.position}</div>}</div>
                              </div>
                            </td>
                            <td style={{ padding: "11px 16px", borderBottom: `1px solid ${t.c.border}` }}>
                              {!p.silent && (
                                <div style={{ display: "flex", gap: 2, height: 10, width: 150, borderRadius: 99, background: t.c.surfaceHover, padding: 2 }}>
                                  {slices.map(([c, m]) => <b key={c} title={`${CATS[c]?.label || c} · ${fmtDur(m)}`} style={{ flex: "none", minWidth: 4, width: `${Math.round((m / tot) * 100)}%`, borderRadius: 99, background: C[c] || t.c.textTertiary }} />)}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: "11px 16px", borderBottom: `1px solid ${t.c.border}` }}>
                              {!p.silent && <div style={{ display: "flex" }}>{(p.topApps || []).slice(0, 3).map((a, j) => (
                                <span key={a.name} title={a.name} style={{ width: 26, height: 26, borderRadius: 8, display: "grid", placeItems: "center", background: t.c.surfaceMuted, border: `1.5px solid ${t.c.surface}`, marginLeft: j ? -6 : 0 }}><ToolIcon name={a.name} size={14} /></span>
                              ))}</div>}
                            </td>
                            <td style={{ padding: "11px 16px", borderBottom: `1px solid ${t.c.border}` }}>
                              {p.silent ? <span style={{ fontSize: 12, color: t.c.textTertiary }}>silent — nothing tracked</span> : (
                                <VerdictPill kind={p.verdict?.kind} />
                              )}
                            </td>
                            <td style={{ padding: "11px 12px", borderBottom: `1px solid ${t.c.border}`, color: t.c.textTertiary }}>{!p.silent && <ChevronRight size={15} />}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
                {!loading && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", fontSize: 12, color: t.c.textTertiary }}>
                    <span>{totals.silent > 0 && (
                      <button type="button" className="st-reset" onClick={() => setShowSilent((v) => !v)} style={{ marginLeft: 8, color: t.c.primary, fontWeight: 600, cursor: "pointer", background: "none", border: 0, padding: 0, font: "inherit" }}>{showSilent ? "hide silent people" : "show silent people"}</button>
                    )}</span>
                    <span />
                  </div>
                )}
              </SectionFrame>

              {/* ── ④ accounting ────────────────────────────────────────── */}
              <SectionFrame kicker="Ledger" title="Time by app" sub="Which applications carried the day, and how many people each touched.">
                {loading ? <div style={{ padding: 20 }}>{[0, 1, 2, 3].map((i) => <Sk key={i} h={38} style={{ marginBottom: 10 }} />)}</div> : (
                  (data.apps || []).slice(0, 12).map((a, i) => {
                    const max = Math.max(1, data.apps[0]?.minutes || 1);
                    return (
                      <div key={a.name} style={{ display: "grid", gridTemplateColumns: "34px 1fr 180px 90px", gap: 14, alignItems: "center", padding: "10px 18px", borderTop: i ? `1px solid ${t.c.border}` : "none" }}>
                        <span style={{ width: 30, height: 30, borderRadius: 9, background: t.c.surfaceMuted, display: "grid", placeItems: "center" }}><ToolIcon name={a.name} size={16} cat={a.cat} /></span>
                        <div><div style={{ fontSize: 13.5, fontWeight: 600, color: t.c.text }}>{a.name}</div><div style={{ fontSize: 11.5, color: t.c.textTertiary }}>{CATS[a.cat]?.label || a.cat} · {a.people} {a.people === 1 ? "person" : "people"}</div></div>
                        <div style={{ height: 10, borderRadius: 99, background: t.c.surfaceHover, padding: 2 }}><b style={{ display: "block", height: "100%", width: `${Math.round((a.minutes / max) * 100)}%`, borderRadius: 99, background: C[a.cat] || t.c.textTertiary }} /></div>
                        <div />
                      </div>
                    );
                  })
                )}
              </SectionFrame>
              <div style={{ fontSize: 12, color: t.c.textTertiary, margin: "10px 4px 0" }}>People with no department land in "Unassigned" — they are never hidden.</div>
            </React.Fragment>
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

export default DepartmentStory;
